import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, Clock, Mail, X, Trophy, Truck } from "lucide-react";

export default function BuyRequestStatusPage() {
    const { id } = useParams();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = async () => {
        try {
            const url = token
                ? `/buy-requests/${id}?token=${encodeURIComponent(token)}`
                : `/buy-requests/${id}`;
            const r = await api.get(url);
            setData(r.data);
            setErr("");
        } catch (e) {
            setErr(e.response?.data?.detail || "Anfrage konnte nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-line */ }, [id, token]);

    const accept = async (offerId) => {
        if (!window.confirm("Dieses Angebot annehmen? Alle anderen Angebote werden automatisch abgelehnt.")) return;
        try {
            await api.post(`/buy-requests/${id}/accept?token=${encodeURIComponent(token)}`, { offer_id: offerId });
            toast.success("Angebot angenommen — der Händler wurde benachrichtigt!");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Annehmen fehlgeschlagen");
        }
    };

    const cancel = async () => {
        if (!window.confirm("Anfrage wirklich zurückziehen? Diese Aktion kann nicht rückgängig gemacht werden.")) return;
        try {
            await api.post(`/buy-requests/${id}/cancel?token=${encodeURIComponent(token)}`);
            toast.success("Anfrage zurückgezogen");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler");
        }
    };

    if (loading) return <div className="max-w-3xl mx-auto py-12 px-6">Lädt…</div>;
    if (err) return (
        <div className="max-w-3xl mx-auto py-16 px-6 text-center" data-testid="status-error">
            <X className="w-12 h-12 text-[#DC2626] mx-auto mb-3" />
            <h2 className="font-display text-2xl font-bold">Kein Zugriff</h2>
            <p className="text-gray-600 mt-2">{err}</p>
            <Link to="/ankauf" className="inline-block mt-6 text-[#2563EB] font-bold hover:underline">→ Neue Anfrage starten</Link>
        </div>
    );
    if (!data) return null;

    const offers = (data.offers || []).slice().sort((a, b) => b.amount - a.amount);
    const statusBadge = {
        open: { t: "Offen", c: "bg-[#FFCC00] text-[#111827]" },
        accepted: { t: "Angenommen", c: "bg-green-100 text-green-700" },
        cancelled: { t: "Zurückgezogen", c: "bg-gray-100 text-gray-600" },
        expired: { t: "Abgelaufen", c: "bg-gray-100 text-gray-600" },
    }[data.status] || { t: data.status, c: "bg-gray-100" };

    return (
        <div className="bg-[#F8F9FA] min-h-screen pb-16" data-testid="status-page">
            <div className="max-w-3xl mx-auto px-6 py-10">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <Badge className={`${statusBadge.c} hover:${statusBadge.c} rounded-sm uppercase tracking-widest text-[10px] font-bold`}>{statusBadge.t}</Badge>
                        <h1 className="font-display text-3xl font-black mt-2">{data.brand} {data.model}</h1>
                        <div className="text-sm text-gray-600">{data.year} · PLZ {data.postal_code}{data.mileage_km ? ` · ${data.mileage_km.toLocaleString("de-DE")} km` : ""}</div>
                    </div>
                    {data.status === "open" && (
                        <Button onClick={cancel} variant="outline" className="rounded-sm font-bold border-[#DC2626] text-[#DC2626] hover:bg-red-50" data-testid="btn-cancel">
                            Zurückziehen
                        </Button>
                    )}
                </div>

                {/* Photos */}
                <div className="grid grid-cols-3 gap-2 mb-6">
                    {["img_front", "img_left", "img_right", "img_back", "img_dashboard", "img_damage", "img_papers"].map((k) =>
                        data[k] ? <img key={k} src={data[k]} alt={k} className="aspect-square object-cover rounded-sm border border-gray-200" /> : null
                    )}
                </div>

                {/* Offers */}
                <div className="bg-white border border-gray-200 rounded-sm p-6">
                    <h2 className="font-display text-xl font-bold mb-1 flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-[#FFCC00]" /> Angebote ({offers.length})
                    </h2>
                    <p className="text-xs text-gray-600 mb-4">Sortiert nach Höhe — das beste Angebot oben.</p>

                    {offers.length === 0 ? (
                        <div className="text-center py-10">
                            <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                            <p className="text-sm font-bold">Noch keine Angebote</p>
                            <p className="text-xs text-gray-500 mt-1">Händler haben 48 Stunden Zeit zum Bieten. Wir senden Ihnen bei jedem neuen Angebot eine E-Mail.</p>
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="offers-list">
                            {offers.map((o, idx) => {
                                const isWinner = data.winning_offer_id === o.id;
                                const isLost = o.status === "lost";
                                return (
                                    <div key={o.id} className={`border rounded-sm p-4 ${isWinner ? "border-green-500 bg-green-50" : isLost ? "border-gray-200 opacity-50" : "border-gray-200"}`} data-testid={`offer-${o.id}`}>
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    {idx === 0 && data.status === "open" && <Badge className="bg-[#FFCC00] text-[#111827] hover:bg-[#FFCC00] text-[10px] font-bold rounded-sm">HÖCHSTES</Badge>}
                                                    {isWinner && <Badge className="bg-green-600 text-white hover:bg-green-600 text-[10px] font-bold rounded-sm"><CheckCircle2 className="w-3 h-3 mr-1" /> Angenommen</Badge>}
                                                    <span className="font-bold">{o.dealer_name}</span>
                                                </div>
                                                <div className="text-3xl font-black font-mono-num mt-2">{Math.round(o.amount).toLocaleString("de-DE")} €</div>
                                                {o.message && <p className="text-sm text-gray-700 mt-2">{o.message}</p>}
                                                <div className="flex flex-wrap gap-3 text-xs text-gray-500 mt-2">
                                                    {o.pickup_offered && <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> Abholung angeboten</span>}
                                                    {o.valid_until && <span>Gültig bis {o.valid_until}</span>}
                                                    <span>Eingegangen: {new Date(o.created_at).toLocaleString("de-DE")}</span>
                                                </div>
                                            </div>
                                            {data.status === "open" && !isLost && (
                                                <Button onClick={() => accept(o.id)} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider" data-testid={`btn-accept-${o.id}`}>
                                                    Annehmen
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {data.status === "accepted" && (
                    <div className="mt-6 bg-green-50 border border-green-200 rounded-sm p-6" data-testid="status-accepted">
                        <div className="flex items-start gap-3">
                            <Mail className="w-6 h-6 text-green-700 flex-shrink-0 mt-1" />
                            <div>
                                <h3 className="font-bold text-green-900">Glückwunsch — Deal abgeschlossen!</h3>
                                <p className="text-sm text-green-800 mt-1">Der Gewinner-Händler hat Ihre Kontaktdaten per E-Mail erhalten und meldet sich zeitnah bei Ihnen zur Terminvereinbarung.</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
